import { Box, Typography } from '@mui/material';
import { useAnalyzeAudience } from 'client/modules/Analyze/AnalyzeAudienceContext';
import {
  BLUE,
  cardSx,
  INK,
  MUTED,
  scoreColor,
  SECONDARY,
} from 'client/modules/Analyze/analyzeTheme';
import AnalyzeAudienceControls from 'client/modules/Analyze/components/AnalyzeAudienceControls';
import { useGetFollowUpQuestionsQuery } from 'client/modules/Surveys/surveyAnalyzeApiSlice';
import ActivityIndicator from 'client/modules/App/components/ActivityIndicator/ActivityIndicator';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useOutletContext } from 'react-router-dom';

const messages = defineMessages({
  loadError: {
    id: 'analyze.followUp.loadError',
    defaultMessage: 'Failed to load follow-up questions',
  },
  noQuestions: {
    id: 'analyze.followUp.noQuestions',
    defaultMessage: 'No follow-up questions configured for this survey',
  },
  noResponses: {
    id: 'analyze.followUp.noResponses',
    defaultMessage: 'No responses for this question in the selected period',
  },
  responses: {
    id: 'analyze.followUp.responses',
    defaultMessage: '{count, plural, one {# response} other {# responses}}',
  },
  freeResponse: {
    id: 'analyze.followUp.freeResponse',
    defaultMessage: 'Free text responses',
  },
});

const titleSx = { m: 0, fontSize: 17, fontWeight: 700, color: INK };
const subtitleSx = { fontSize: 13, color: MUTED, mt: 0.5 };

// Vertical bars for a numeric scale question.
function ScaleBars({ options, total, formatNumber }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 180, mt: 2.5 }}>
      {options.map(option => {
        const pct = total > 0 ? Math.round((option.count / total) * 1000) / 10 : 0;
        const value = Number(option.optionId);
        const color = Number.isNaN(value) ? BLUE : scoreColor(value, 'star_five');
        return (
          <Box
            key={option.optionId}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100%',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              sx={{ fontSize: 18, fontWeight: 800, color: INK, fontVariantNumeric: 'tabular-nums' }}
            >
              {formatNumber(pct)}%
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 64,
                borderRadius: '6px 6px 0 0',
                bgcolor: color,
                height: `${Math.max(2, pct)}%`,
                minHeight: option.count > 0 ? 4 : 0,
                mt: 0.75,
              }}
              title={`${option.label}: ${formatNumber(option.count)}`}
            />
            <Typography
              sx={{ fontSize: 12.5, fontWeight: 600, color: SECONDARY, mt: 1, textAlign: 'center' }}
            >
              {option.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// Horizontal bars for choice questions.
function ChoiceBars({ options, total, formatNumber }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2.5 }}>
      {options.map(option => {
        const pct = total > 0 ? Math.round((option.count / total) * 1000) / 10 : 0;
        return (
          <Box key={option.optionId || option.label}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK }}>
                {option.label}
              </Typography>
              <Typography sx={{ fontSize: 13, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                {formatNumber(pct)}% · {formatNumber(option.count)}
              </Typography>
            </Box>
            <Box sx={{ height: 12, bgcolor: '#EEF1F5', borderRadius: '6px', overflow: 'hidden' }}>
              <Box sx={{ width: `${Math.max(0, pct)}%`, height: '100%', bgcolor: BLUE }} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function FollowUpQuestionCard({ question }) {
  const { formatMessage, formatNumber } = useIntl();
  const total = question.responses ?? 0;
  const hasOptions = question.options?.length > 0;
  const isScale = question.type === 'scale';

  return (
    <Box sx={{ ...cardSx, mb: 2.25 }}>
      <Typography sx={titleSx}>{question.text}</Typography>
      <Typography sx={subtitleSx}>{formatMessage(messages.responses, { count: total })}</Typography>
      {hasOptions && total > 0 ? (
        isScale ? (
          <ScaleBars options={question.options} total={total} formatNumber={formatNumber} />
        ) : (
          <ChoiceBars options={question.options} total={total} formatNumber={formatNumber} />
        )
      ) : (
        <Typography sx={{ fontSize: 14, color: SECONDARY, mt: 2 }}>
          {total > 0 ? formatMessage(messages.freeResponse) : formatMessage(messages.noResponses)}
        </Typography>
      )}
    </Box>
  );
}

function AnalyzeFollowUpQuestionsPage() {
  const { survey: outletSurvey } = useOutletContext();
  const { survey, audienceParams } = useAnalyzeAudience();
  const activeSurvey = survey || outletSurvey;
  const { formatMessage } = useIntl();

  const query = useMemo(
    () => ({ survey: activeSurvey?.id, ...audienceParams }),
    [activeSurvey?.id, audienceParams],
  );

  const { data, isFetching, isError } = useGetFollowUpQuestionsQuery(query, {
    skip: !activeSurvey?.id,
  });

  if (!activeSurvey) {
    return null;
  }

  const questions = data?.questions || [];

  return (
    <Box>
      <AnalyzeAudienceControls />
      {isFetching && !data && <ActivityIndicator />}
      {isError && !data && (
        <Typography color="error">{formatMessage(messages.loadError)}</Typography>
      )}
      {!isFetching && questions.length === 0 && (
        <Typography>{formatMessage(messages.noQuestions)}</Typography>
      )}
      {questions.map(question => (
        <FollowUpQuestionCard key={question.id} question={question} />
      ))}
    </Box>
  );
}

export default AnalyzeFollowUpQuestionsPage;
